import translation from 'locales/translation';
import React, { useCallback } from 'react';

import {
  ParametroCoopRequest,
  ParametrosCooperativa,
  ParametrosTexto,
} from 'api/Assessor/parametros';
import { getUniqueKey } from 'helpers/utils/KeyUtils';
import { useDispatch } from 'react-redux';

import { modalsActionsOpen } from 'store/modules/ModalsActions';

import {
  Button,
  Checkbox,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from '@material-ui/core';
import {
  ChatIcon,
  CircleTimes,
  DropdownIcon,
  EditIcon,
} from 'components/Icons';
import CadastrarTextoAssociadoModal, {
  ICadastrarTextoAssociadoModalGenericProps,
} from 'components/Modal/__LEGACY__/CadastrarTextoAssociadoModal/CadastrarTextoAssociadoModal';

import RespostaPadraoModal from 'components/Modal/__LEGACY__/RespostaPadraoModal/RespostaPadraoModal';
import { ParametroTipoResultado } from 'helpers/enums/Parametros';
import useStyle from './style';

interface Props {
  items: Array<ParametrosCooperativa>;
  size: number;
  page: number;
  count: number;
  onChangeParametro: (item: ParametroCoopRequest) => void;
  onChangeParametroTexto: (item: ParametrosTexto) => void;
  onDelete: (id: number) => void;
  onChangePagination: (size: number, page: number) => void;
}

export default function Tabela({
  items,
  onChangeParametro,
  onChangeParametroTexto,
  onDelete,
  onChangePagination,
  count,
  size,
  page,
}: Props) {
  const classes = useStyle();
  const dispatch = useDispatch();

  const handleChangePage = useCallback(
    (_: React.MouseEvent<HTMLButtonElement> | null, value: number) => {
      onChangePagination(size, value + 1);
    },
    [size, onChangePagination]
  );

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChangePagination(parseInt(event.target.value, 10), 1);
    },
    [onChangePagination]
  );

  const handleChangeFlag = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      const data = JSON.parse(event.target.value) as ParametrosCooperativa;
      const request: ParametroCoopRequest = {
        idParametro: data.idParametroCoop,
        configTexto: data.configTexto,
        ocultarNomeChat: data.ocultarNomeChat,
        bloqAlteraVoto: data.bloqAlteraVoto,
        tipoResultado: data.tipoResultado,
      };
      request[event.target.name] = checked;
      onChangeParametro(request);
    },
    [onChangeParametro]
  );

  const handleChangeTipoResultado = useCallback(
    (item: ParametrosCooperativa) => {
      return (
        e: React.ChangeEvent<{ name?: string | undefined; value: unknown }>
      ) => {
        const tipoResultado = e.target.value as ParametroTipoResultado;
        onChangeParametro({
          bloqAlteraVoto: item.bloqAlteraVoto,
          configTexto: item.configTexto,
          idParametro: item.idParametroCoop,
          ocultarNomeChat: item.ocultarNomeChat,
          tipoResultado,
        });
      };
    },
    [onChangeParametro]
  );

  const handleChangeTextoAssociado = useCallback(
    ({ idParametro, texto, urlVideo }: ParametrosTexto) => {
      const modalKey: string = getUniqueKey();
      dispatch(
        modalsActionsOpen(
          CadastrarTextoAssociadoModal,
          {
            genericProps: {
              idParametro,
              texto,
              urlVideo,
              key: modalKey,
              title: translation.Parâmetros.modalConfigurarTexto.titulo,
              onConfirm: onChangeParametroTexto,
            } as ICadastrarTextoAssociadoModalGenericProps,
          },
          modalKey
        )
      );
    },
    [dispatch, onChangeParametroTexto]
  );

  const respostaPadraoModal = useCallback(
    (codCooperativa: number | string, idParametroCoop: number | string) => {
      const modalKey: string = getUniqueKey();
      dispatch(
        modalsActionsOpen(
          RespostaPadraoModal,
          {
            genericProps: {
              key: modalKey,
              codCoop: codCooperativa,
              idParametroCoop: idParametroCoop,
            } /* as IAcompanharVotacaoPautaModalGenericProps, */,
          },
          modalKey
        )
      );
    },
    [dispatch]
  );

  return (
    <TableContainer className={classes.root}>
      <Table>
        <TableHead className={classes.box}>
          <TableRow>
            <TableCell className={classes.title} align='center'>
              {translation.Parâmetros.headerTabela.coop}
            </TableCell>
            <TableCell className={classes.title} align='left'>
              {translation.Parâmetros.headerTabela.nomeCoop}
            </TableCell>
            <TableCell className={classes.title} align='center'>
              {translation.Parâmetros.headerTabela.flagAlterarVoto}
            </TableCell>
            <TableCell className={classes.title} align='center'>
              {translation.Parâmetros.headerTabela.tipoResultado}
            </TableCell>
            <TableCell className={classes.title} align='center'>
              {translation.Parâmetros.headerTabela.flagOcultarNome}
            </TableCell>
            <TableCell className={classes.title} colSpan={2} align='left'>
              {translation.Parâmetros.headerTabela.flagAlterarTexto}
            </TableCell>
            <TableCell
              className={[classes.title, classes.deleteCell].join(' ')}
              align='center'
            >
              {translation.Parâmetros.headerTabela.respostaPadrao}
            </TableCell>
            <TableCell
              className={[classes.title, classes.deleteCell].join(' ')}
              align='center'
            >
              {translation.Parâmetros.headerTabela.acaoExcluir}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody className={classes.box}>
          {items.map((row) => (
            <TableRow key={row.idParametroCoop}>
              <TableCell className={classes.data} align='center'>
                {row.codCooperativa}
              </TableCell>
              <TableCell className={classes.data} align='left'>
                {row.nomeCooperativa}
              </TableCell>
              <TableCell className={classes.data} align='center'>
                <Checkbox
                  color='primary'
                  name='bloqAlteraVoto'
                  value={JSON.stringify(row)}
                  onChange={handleChangeFlag}
                  checked={row.bloqAlteraVoto}
                />
              </TableCell>
              <TableCell>
                <TextField
                  SelectProps={{
                    IconComponent: () => (
                      <DropdownIcon className={classes.selectIcon} />
                    ),
                    value: row.tipoResultado,
                    onChange: handleChangeTipoResultado(row),
                  }}
                  InputProps={{
                    disableUnderline: true,
                  }}
                  select
                  fullWidth
                >
                  <MenuItem
                    value={ParametroTipoResultado.agenciaNucleo}
                    selected={
                      row.tipoResultado === ParametroTipoResultado.agenciaNucleo
                    }
                  >
                    {translation.Parâmetros.tipoResultado.agenciaNucleo}
                  </MenuItem>
                  <MenuItem
                    value={ParametroTipoResultado.agencia}
                    selected={
                      row.tipoResultado === ParametroTipoResultado.agencia
                    }
                  >
                    {translation.Parâmetros.tipoResultado.agencia}
                  </MenuItem>
                  <MenuItem
                    value={ParametroTipoResultado.evento}
                    selected={
                      row.tipoResultado === ParametroTipoResultado.evento
                    }
                  >
                    {translation.Parâmetros.tipoResultado.evento}
                  </MenuItem>
                </TextField>
              </TableCell>
              <TableCell className={classes.data} align='center'>
                <Checkbox
                  color='primary'
                  name='ocultarNomeChat'
                  value={JSON.stringify(row)}
                  onChange={handleChangeFlag}
                  checked={row.ocultarNomeChat}
                />
              </TableCell>
              <TableCell className={classes.data} align='left'>
                <Checkbox
                  color='primary'
                  name='configTexto'
                  value={JSON.stringify(row)}
                  onChange={handleChangeFlag}
                  checked={row.configTexto}
                />
              </TableCell>
              <TableCell className={classes.data} align='left'>
                <Button
                  variant='contained'
                  color='primary'
                  onClick={() =>
                    handleChangeTextoAssociado({
                      idParametro: row.idParametroCoop,
                      texto: row.texto,
                      urlVideo: row.urlVideo,
                    })
                  }
                  startIcon={<EditIcon />}
                  disabled={row.configTexto === false}
                >
                  {translation.Parâmetros.buttonConfigTextLabel}
                </Button>
              </TableCell>
              <TableCell align='center'>
                <IconButton
                  color='primary'
                  onClick={() =>
                    respostaPadraoModal(row.codCooperativa, row.idParametroCoop)
                  }
                  className={classes.deleteButton}
                >
                  <ChatIcon />
                </IconButton>
              </TableCell>
              <TableCell
                className={[classes.data, classes.deleteCell].join(' ')}
                align='center'
              >
                <IconButton
                  color='primary'
                  onClick={() => onDelete(row.idParametroCoop)}
                  className={classes.deleteButton}
                >
                  <CircleTimes />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              style={{ borderBottom: 'none' }}
              count={count}
              colSpan={9}
              rowsPerPageOptions={[10, 25, 50, 100]}
              rowsPerPage={size}
              page={page > 0 ? page - 1 : 0}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelDisplayedRows={({ from, to, count }) =>
                translation.RelatorioPresentes.labelDisplayedRows(
                  from,
                  to,
                  count
                )
              }
              labelRowsPerPage={translation.RelatorioPresentes.labelRowsPerPage}
              backIconButtonProps={{ className: classes.navIcon }}
              nextIconButtonProps={{ className: classes.navIcon }}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}
